import React, { useState } from 'react';
import { Clock, ChefHat, Users, Flame, ThumbsUp, ThumbsDown, AlertTriangle } from 'lucide-react';
import { Recipe as RecipeType, MeasurementSystem } from '../types';
import { convertMeasurement } from '../utils/measurements';
import RecipeActions from './RecipeActions';

interface RecipeProps {
  recipe: RecipeType;
  measurementSystem: MeasurementSystem;
}

function Recipe({ recipe, measurementSystem }: RecipeProps) {
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);
  const [feedbackNote, setFeedbackNote] = useState<string>('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const handleFeedback = (type: 'like' | 'dislike') => {
    if (type === 'like') {
      setFeedbackSubmitted(true);
    }
    setFeedback(type);
  };

  const handleFeedbackSubmit = () => {
    if (feedbackNote.trim()) {
      setFeedbackSubmitted(true);
    }
  };

  const validateRecipe = () => {
    const warnings: string[] = [];
    
    recipe.ingredients.forEach(ing => {
      const amount = parseFloat(ing.amount);
      if (amount > 1000 && ['g', 'ml'].includes(ing.unit.toLowerCase())) {
        warnings.push(`Large quantity warning: ${amount}${ing.unit} of ${ing.name}`);
      }
    });

    const cookTime = parseInt(recipe.cookTime);
    if (cookTime > 120) {
      warnings.push('Unusually long cooking time');
    }

    return warnings;
  };

  const warnings = validateRecipe();

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h2 className="text-3xl font-heading text-text mb-3">{recipe.title}</h2>
          <p className="text-text-light text-lg font-body">{recipe.description}</p>
        </div>

        {warnings.length > 0 && (
          <div className="mb-6 bg-secondary p-4 rounded-lg">
            <div className="flex items-center gap-2 text-primary mb-2 font-body">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Recipe Warnings</span>
            </div>
            <ul className="space-y-1 text-sm text-text-light font-body">
              {warnings.map((warning, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="block w-1.5 h-1.5 mt-1.5 rounded-full bg-primary flex-shrink-0" />
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="flex items-center gap-2 bg-secondary p-4 rounded-lg">
            <Clock className="w-5 h-5 text-primary" />
            <div className="font-body">
              <p className="text-sm text-text-light">Prep Time</p>
              <p className="font-semibold text-text">{recipe.prepTime}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 bg-secondary p-4 rounded-lg">
            <Flame className="w-5 h-5 text-primary" />
            <div className="font-body">
              <p className="text-sm text-text-light">Cook Time</p>
              <p className="font-semibold text-text">{recipe.cookTime}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-secondary p-4 rounded-lg">
            <ChefHat className="w-5 h-5 text-primary" />
            <div className="font-body">
              <p className="text-sm text-text-light">Difficulty</p>
              <p className="font-semibold text-text">{recipe.difficulty}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-secondary p-4 rounded-lg">
            <Users className="w-5 h-5 text-primary" />
            <div className="font-body">
              <p className="text-sm text-text-light">Servings</p>
              <p className="font-semibold text-text">{recipe.servings}</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-heading mb-4 text-text">Ingredients</h3>
            <ul className="space-y-3 font-body">
              {recipe.ingredients.map((ingredient, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2"></span>
                  <span className="text-text">
                    {convertMeasurement(ingredient.amount, ingredient.unit, measurementSystem)}{' '}
                    {ingredient.name}
                    {ingredient.notes && (
                      <span className="text-text-light italic"> ({ingredient.notes})</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-heading mb-4 text-text">Instructions</h3>
            <ol className="space-y-4 font-body">
              {recipe.instructions.map((instruction, index) => (
                <li key={index} className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-primary font-semibold">
                    {index + 1}
                  </span>
                  <p className="text-text">{instruction}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {recipe.notes && recipe.notes.length > 0 && (
          <div className="mt-8 bg-secondary p-6 rounded-lg">
            <h3 className="text-lg font-heading mb-3 text-text">Baker's Notes</h3>
            <ul className="space-y-2 font-body">
              {recipe.notes.map((note, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2"></span>
                  <span className="text-text">{note}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <RecipeActions recipe={recipe} />

        {!feedbackSubmitted && (
          <div className="mt-8 border-t pt-6">
            <h4 className="text-lg font-heading mb-4 text-text">Recipe Feedback</h4>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleFeedback('like')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-body ${
                  feedback === 'like'
                    ? 'bg-green-100 text-green-700'
                    : 'hover:bg-secondary text-text-light'
                }`}
              >
                <ThumbsUp className="w-5 h-5" />
                Recipe Works
              </button>
              <button
                onClick={() => handleFeedback('dislike')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-body ${
                  feedback === 'dislike'
                    ? 'bg-red-100 text-red-700'
                    : 'hover:bg-secondary text-text-light'
                }`}
              >
                <ThumbsDown className="w-5 h-5" />
                Needs Improvement
              </button>
            </div>
            {feedback === 'dislike' && (
              <div className="mt-4">
                <textarea
                  value={feedbackNote}
                  onChange={(e) => setFeedbackNote(e.target.value)}
                  placeholder="What went wrong? Your feedback helps improve future recipes..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-y font-body"
                />
                <button
                  onClick={handleFeedbackSubmit}
                  disabled={!feedbackNote.trim()}
                  className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 font-body"
                >
                  Submit Feedback
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Recipe;